// src/encounter/lib/orphaned-answers.ts
// PROD-2608 — saved answers the form on screen cannot show.
//
// A reopened note renders a template as it stands NOW (templates are edited in place). When that
// template no longer has an item for a saved answer — or, once the template is known to have
// changed, no longer has an item that can hold the saved VALUE — the answer is ORPHANED. Before this
// module such an answer was hydrated into the form's response anyway: invisible (the renderer walks
// the template), and re-serialised on the next autosave with no item metadata, so a coded answer
// was silently rewritten as a bare string. Two ways to lose it without anyone seeing.
//
// The rule is the one the ambient sink follows (Contract E): never dropped, never blocking, always
// visible. An orphan is
//   - NOT hydrated into the form's response (nothing can misplace it into a changed structure),
//   - shown read-only under "Additional documentation", in the encounter and in the note, and
//   - written back VERBATIM on every save and amendment (`preservedItems` below), so the stored
//     answer keeps its exact wire shape: valueCoding stays valueCoding, provenance stays attached.
import type { QRAnswerWire, QRItemWire, QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import type { QrItemMetaMap, QrItemMeta } from './qr-item-meta';
import { codingKey } from './qr-item-meta';
import { ENCOUNTER_NARRATIVE_LINK_ID } from '../ambient/orphan-sink';
import type { NoteSection } from '../generated-note';

/** The heading orphaned answers render under — in the encounter and in the generated note. */
export const ORPHANED_ANSWERS_LABEL = 'Additional documentation';

/** One saved answer the current form has no place for. */
export interface OrphanedAnswer {
  linkId: string;
  /** The question as it read when answered (QR `item.text`), or a plain fallback. Never invented. */
  label: string;
  /** The answer(s) as a clinician reads them. */
  value: string;
  /** Why it has no place: the question is gone, or it no longer takes this kind of answer. */
  reason: 'question-removed' | 'answer-no-longer-fits';
  /** The stored item exactly as saved — written back unchanged on every save. */
  item: QRItemWire;
}

export interface PartitionedResponse {
  /** The saved response minus the orphans — safe to hydrate into the form. */
  hydratable: QuestionnaireResponseResource;
  orphans: OrphanedAnswer[];
}

/**
 * linkIds the app writes OUTSIDE any template on purpose. They have no template item by design, so
 * they are never orphans: the sink renders from the response itself (`generated-note.ts`).
 */
const RESERVED_LINK_IDS = new Set<string>([ENCOUNTER_NARRATIVE_LINK_ID]);

const FALLBACK_LABEL = 'A question no longer in this template';

function formatAnswer(a: QRAnswerWire): string {
  if (a.valueCoding != null) return a.valueCoding.display ?? a.valueCoding.code ?? '';
  if (a.valueBoolean != null) return a.valueBoolean ? 'Yes' : 'No';
  if (a.valueInteger != null) return String(a.valueInteger);
  if (a.valueDecimal != null) return String(a.valueDecimal);
  if (a.valueDateTime != null) {
    const d = new Date(a.valueDateTime);
    return Number.isNaN(d.getTime()) ? a.valueDateTime : d.toLocaleString();
  }
  return a.valueString ?? '';
}

function hasValue(a: QRAnswerWire): boolean {
  return formatAnswer(a).trim() !== '';
}

const NUMERIC = new Set(['integer', 'decimal', 'quantity']);
const DATES = new Set(['date', 'dateTime', 'time']);
const TEXTS = new Set(['string', 'text', 'url']);

/**
 * Can this item, as the template defines it NOW, hold this saved answer? Only asked once the
 * template is known to differ from the one the note was started on — for an unchanged template the
 * saved shapes are the ones this app wrote against it, and second-guessing them could only move
 * good answers out of the form.
 *
 * Unknown item types are given the benefit of the doubt: the check exists to catch a changed
 * structure, not to police types the app does not model.
 */
function fits(answer: QRAnswerWire, meta: QrItemMeta): boolean {
  // The template now says nobody answers this (a readout). The save filter drops read-only items,
  // so hydrating it would delete a clinician-entered answer on the next autosave.
  if (meta.readOnly) return false;
  const t = meta.type;
  if (t === 'group' || t === 'display') return false;
  if (t === 'boolean') return answer.valueBoolean != null;
  if (NUMERIC.has(t)) return answer.valueInteger != null || answer.valueDecimal != null;
  if (DATES.has(t)) return answer.valueDateTime != null;
  if (TEXTS.has(t)) return answer.valueString != null;
  if (t === 'choice' || t === 'open-choice') {
    const key = answer.valueCoding != null
      ? answer.valueCoding.code ?? answer.valueCoding.display ?? undefined
      : answer.valueString ?? undefined;
    if (key === undefined) return false;
    if (t === 'open-choice' && answer.valueCoding == null) return true; // free text is allowed there
    return meta.options.some((o) => codingKey(o) === key);
  }
  return true;
}

/** Flatten the saved item tree to its answered leaves (groups nest via `item`). */
function answeredLeaves(items: QRItemWire[] | undefined | null, out: QRItemWire[] = []): QRItemWire[] {
  for (const it of items ?? []) {
    if ((it.answer ?? []).some(hasValue)) out.push(it);
    if (it.item?.length) answeredLeaves(it.item, out);
  }
  return out;
}

/**
 * Split a saved response into what the current form can show and what it cannot.
 *
 * `strict` — the template on screen is known NOT to be the one the note was started on (its hash
 * changed, or the pinned template is gone and another was picked). Then an answer whose item still
 * exists but can no longer hold its value is an orphan too. Otherwise only a removed question is.
 */
export function partitionSavedResponse(
  saved: QuestionnaireResponseResource,
  itemMeta: QrItemMetaMap,
  strict: boolean,
): PartitionedResponse {
  const orphans: OrphanedAnswer[] = [];
  const orphanLinkIds = new Set<string>();
  for (const it of answeredLeaves(saved.item)) {
    if (RESERVED_LINK_IDS.has(it.linkId)) continue;
    const meta = itemMeta.get(it.linkId);
    const answers = (it.answer ?? []).filter(hasValue);
    const reason: OrphanedAnswer['reason'] | null = !meta
      ? 'question-removed'
      : strict && !answers.every((a) => fits(a, meta))
        ? 'answer-no-longer-fits'
        : null;
    if (!reason) continue;
    orphanLinkIds.add(it.linkId);
    orphans.push({
      linkId: it.linkId,
      label: it.text?.trim() || FALLBACK_LABEL,
      value: answers.map(formatAnswer).join(', '),
      reason,
      item: it,
    });
  }
  if (orphans.length === 0) return { hydratable: saved, orphans };

  const prune = (items: QRItemWire[] | undefined): QRItemWire[] =>
    (items ?? [])
      .filter((it) => !orphanLinkIds.has(it.linkId))
      .map((it) => (it.item ? { ...it, item: prune(it.item) } : it));
  return { hydratable: { ...saved, item: prune(saved.item) }, orphans };
}

/** The stored items to write back unchanged on every save. */
export function preservedItems(orphans: readonly OrphanedAnswer[]): QRItemWire[] {
  return orphans.map((o) => o.item);
}

/**
 * The same orphaned answers as a trailing section of the generated note, so the note a clinician
 * reads and signs carries them too — `responseToNote` walks the template and cannot see them.
 */
export function orphanedAnswersNoteSection(orphans: readonly OrphanedAnswer[]): NoteSection[] {
  if (orphans.length === 0) return [];
  return [
    {
      section: ORPHANED_ANSWERS_LABEL,
      lines: orphans.map((o) => `${o.label}: ${o.value}`),
    },
  ];
}
